import { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Shield, Eye, EyeOff } from 'lucide-react';
import { GpsLicenseGate } from '@/components/gps/GpsLicenseGate';
import { useGpsDevices } from '@/hooks/use-gps-devices';
import { useGpsRealtime } from '@/hooks/use-gps-realtime';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import GoogleLiveMap, { GoogleLiveMapRef } from '@/components/gps/GoogleLiveMap';
import { VehicleDrawer } from '@/components/gps/VehicleDrawer';
import { RouteDetailsSheet } from '@/components/gps/RouteDetailsSheet';
import { StatisticsFooter } from '@/components/gps/StatisticsFooter';
import { ReplayControls } from '@/components/gps/ReplayControls';
import { TrajectoryLegend } from '@/components/gps/TrajectoryLegend';
import { GeofenceSheet } from '@/components/gps/GeofenceSheet';
import { useGpsTrips } from '@/hooks/use-gps-trips';
import { useGeofences } from '@/hooks/use-geofences';
import { startOfDay, endOfDay } from 'date-fns';
import { toast } from 'sonner';

export default function GpsLive() {
  const { effectiveCompanyId } = useAuth();
  const { data: devices = [], isLoading, refetch } = useGpsDevices(effectiveCompanyId);
  const mapRef = useRef<GoogleLiveMapRef>(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>();
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [companyId, setCompanyId] = useState<string>();
  const [routeDetailsDeviceId, setRouteDetailsDeviceId] = useState<string>();
  const [lastRouteDetailsDeviceId, setLastRouteDetailsDeviceId] = useState<string>();
  const [routePolyline, setRoutePolyline] = useState<Array<{ lat: number; lng: number }>>([]);
  const [coloredRoutePoints, setColoredRoutePoints] = useState<Array<{
    lat: number;
    lng: number;
    speed_kmh: number | null;
    fix_time: string;
  }>>([]);
  const [showColoredRoute, setShowColoredRoute] = useState(false);
  const [dateRange] = useState({
    start: startOfDay(new Date()).toISOString(),
    end: endOfDay(new Date()).toISOString(),
  });
  
  // Drawing states
  const [drawingMode, setDrawingMode] = useState<'none' | 'circle' | 'polygon'>('none');
  const [drawnGeofence, setDrawnGeofence] = useState<any>(null);

  // Replay states
  const [replayMode, setReplayMode] = useState(false);
  const [replayPositions, setReplayPositions] = useState<Array<{
    id: string;
    fix_time: string;
    lat: number;
    lng: number;
    speed_kmh: number | null;
    course_deg: number | null;
    ignition: boolean | null;
  }>>([]);
  const [replayCurrentIndex, setReplayCurrentIndex] = useState(0);
  const [replayIsPlaying, setReplayIsPlaying] = useState(false);
  const [replaySpeed, setReplaySpeed] = useState(1);

  // Geofence states
  const [geofenceSheetOpen, setGeofenceSheetOpen] = useState(false);
  const [showGeofences, setShowGeofences] = useState(true);

  // Enable real-time updates
  useGpsRealtime(effectiveCompanyId);

  // Fetch geofences
  const { geofences } = useGeofences(companyId);

  // Buscar companyId do usuário autenticado
  useEffect(() => {
    const fetchCompanyId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      const { data } = await supabase
        .from('user_companies')
        .select('company_id')
        .eq('user_id', user.id)
        .single();
      
      if (data) setCompanyId(data.company_id);
    };
    
    fetchCompanyId();
  }, []);

  // Auto-refresh a cada 5 segundos
  useEffect(() => {
    const interval = setInterval(() => refetch(), 5000);
    return () => clearInterval(interval);
  }, [refetch]);

  // Buscar trips do device selecionado
  const { data: selectedDeviceTrips = [] } = useGpsTrips(
    effectiveCompanyId,
    {
      deviceId: routeDetailsDeviceId,
      startDate: dateRange.start,
      endDate: dateRange.end,
    }
  );

  // Calcular estatísticas do device selecionado
  const deviceStats = useMemo(() => {
    if (!routeDetailsDeviceId) return null;
    
    const totalDistance = selectedDeviceTrips.reduce((sum, t) => sum + (t.distance_km || 0), 0);
    const maxSpeed = Math.max(...selectedDeviceTrips.map((t) => (t as any).max_speed_kmh || 0), 0);
    const totalMinutes = selectedDeviceTrips.reduce((sum, t) => sum + (t.duration_minutes || 0), 0);
    const idleMinutes = selectedDeviceTrips.reduce((sum, t) => sum + (t.idle_time_minutes || 0), 0);
    
    return { totalDistance, maxSpeed, totalMinutes, idleMinutes };
  }, [selectedDeviceTrips, routeDetailsDeviceId]);

  // Buscar device selecionado para RouteDetailsSheet
  const selectedDevice = devices.find((d) => d.id === routeDetailsDeviceId);

  // Callback para atualizar polyline
  const handlePolylineUpdate = (positions: Array<{ lat: number; lng: number }>) => {
    setRoutePolyline(positions);
  };

  const handleColoredPolylineUpdate = (points: typeof coloredRoutePoints) => {
    setColoredRoutePoints(points);
    setShowColoredRoute(true);
  };

  const handleViewTrajectory = (tripId: string) => {
    console.log('Visualizando trajeto da viagem:', tripId);
  };

  // Limpar polyline quando fechar RouteDetailsSheet
  const handleRouteDetailsClose = (open: boolean) => {
    if (!open) {
      setRouteDetailsDeviceId(undefined);
      setRoutePolyline([]);
    }
  };

  // Replay handlers
  const handleStartReplay = (positions: typeof replayPositions) => {
    setLastRouteDetailsDeviceId(routeDetailsDeviceId);
    setReplayMode(true);
    setReplayPositions(positions);
    setReplayCurrentIndex(0);
    setReplayIsPlaying(true);
    setRouteDetailsDeviceId(undefined);
    setRoutePolyline(positions.map(p => ({ lat: p.lat, lng: p.lng })));
    setShowColoredRoute(false);
    setColoredRoutePoints([]);
  };

  const handleStopReplay = () => {
    setReplayMode(false);
    setReplayPositions([]);
    setReplayCurrentIndex(0);
    setReplayIsPlaying(false);
    setReplaySpeed(1);
    setRoutePolyline([]); // Clear polyline
    
    // Reopen RouteDetailsSheet with the device that was open
    if (lastRouteDetailsDeviceId) {
      setRouteDetailsDeviceId(lastRouteDetailsDeviceId);
    }
  };

  // Replay animation
  useEffect(() => {
    if (!replayIsPlaying || replayCurrentIndex >= replayPositions.length - 1) {
      if (replayCurrentIndex >= replayPositions.length - 1) {
        setReplayIsPlaying(false);
      }
      return;
    }

    const interval = setInterval(() => {
      setReplayCurrentIndex(prev => Math.min(prev + 1, replayPositions.length - 1));
    }, 1000 / replaySpeed);

    return () => clearInterval(interval);
  }, [replayIsPlaying, replayCurrentIndex, replayPositions.length, replaySpeed]);

  // Fechar VehicleInfoWindow quando RouteDetailsSheet abre
  useEffect(() => {
    if (routeDetailsDeviceId) {
      setSelectedDeviceId(undefined);
    }
  }, [routeDetailsDeviceId]);

  // Clear colored route when RouteDetailsSheet closes
  useEffect(() => {
    if (!routeDetailsDeviceId) {
      setShowColoredRoute(false);
      setColoredRoutePoints([]);
    }
  }, [routeDetailsDeviceId]);

  return (
    <GpsLicenseGate>
      <div 
        className="relative w-full overflow-hidden"
        style={{ height: '100vh' }}
      >
        {/* Badge de modo desenho ativo */}
        {drawingMode !== 'none' && (
          <div className="fixed top-20 left-4 z-30 bg-card border-2 border-primary shadow-lg rounded-lg px-4 py-3 flex items-center gap-3 animate-in fade-in slide-in-from-top-5">
            <span className="text-2xl">🎯</span>
            <div className="flex flex-col gap-1">
              <p className="font-semibold text-sm">
                Modo desenho ativo: {drawingMode === 'circle' ? 'Círculo' : 'Polígono'}
              </p>
              <p className="text-xs text-muted-foreground">
                {drawingMode === 'circle' 
                  ? 'Clique e arraste para desenhar. Use a alça para ajustar o raio. Pressione Enter para confirmar ou Esc para cancelar.'
                  : 'Clique para adicionar vértices. Clique no botão "Concluir" quando terminar.'}
              </p>
            </div>
            <div className="flex items-center gap-2 ml-2">
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  // @ts-ignore - mapRef has confirmCircle method
                  mapRef.current?.confirmCircle?.();
                  setDrawingMode('none');
                }}
              >
                Confirmar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // @ts-ignore - mapRef has cancelCircle method
                  mapRef.current?.cancelCircle?.();
                  setDrawingMode('none');
                }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {/* Container do mapa - sem margin para evitar gap branco */}
        <div
          className="absolute inset-0 transition-all duration-300 ease-in-out"
          style={{ 
            bottom: routeDetailsDeviceId ? '48px' : '0px'
          }}
        >
      <GoogleLiveMap
        ref={mapRef}
        devices={devices}
        selectedDeviceId={selectedDeviceId}
        onDeviceSelect={setSelectedDeviceId}
        onRouteDetailsClick={setRouteDetailsDeviceId}
        companyId={companyId}
        routePolyline={replayMode ? routePolyline : undefined}
        coloredRoutePoints={showColoredRoute && !replayMode ? coloredRoutePoints : undefined}
        showStopMarkers={showColoredRoute && !replayMode}
        replayMode={replayMode}
        replayPositions={replayPositions}
        replayCurrentIndex={replayCurrentIndex}
        geofences={geofences}
        showGeofences={showGeofences}
        drawingMode={drawingMode}
        onGeofenceDrawn={(coords) => {
          setDrawnGeofence(coords);
          setDrawingMode('none'); // Sai do modo desenho para fixar o círculo
        }}
      />
        </div>

        {/* Botões flutuantes de Geocerca - Canto superior esquerdo */}
        <div className="absolute top-4 left-4 z-30 flex gap-2">
          <Button
            onClick={() => setGeofenceSheetOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg"
            size="sm"
          >
            <Shield className="h-4 w-4 mr-2" />
            Geocercas
          </Button>

          <Button
            variant="outline"
            onClick={() => setShowGeofences(!showGeofences)}
            className="bg-white shadow-lg"
            size="sm"
          >
            {showGeofences ? (
              <>
                <EyeOff className="h-4 w-4 mr-2" />
                Ocultar cercas
              </>
            ) : (
              <>
                <Eye className="h-4 w-4 mr-2" />
                Mostrar cercas
              </>
            )}
          </Button>
        </div>

        {/* Badge de Modo Desenho Ativo */}
        {drawingMode !== 'none' && (
          <div className="absolute top-20 left-4 z-30">
            <div className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              <span className="font-semibold">
                🎯 Modo desenho ativo: {drawingMode === 'circle' ? 'Círculo' : 'Polígono'}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDrawingMode('none')}
                className="ml-2 h-6 w-6 p-0 hover:bg-white/20 text-white"
              >
                ✕
              </Button>
            </div>
            <p className="text-xs text-white bg-black/50 px-2 py-1 rounded mt-1">
              {drawingMode === 'circle' 
                ? 'Clique para definir centro, depois clique novamente para definir raio'
                : 'Clique para adicionar vértices. Clique no botão "Concluir" quando terminar'}
            </p>
          </div>
        )}

        {/* Trajectory Legend */}
        {showColoredRoute && <TrajectoryLegend />}

        {/* Replay Controls */}
        {replayMode && replayPositions.length > 0 && (
          <ReplayControls
            isPlaying={replayIsPlaying}
            onPlayPause={() => setReplayIsPlaying(!replayIsPlaying)}
            onReset={() => { setReplayCurrentIndex(0); setReplayIsPlaying(false); }}
            speed={replaySpeed}
            onSpeedChange={setReplaySpeed}
        currentIndex={replayCurrentIndex}
        totalPositions={replayPositions.length}
        currentPosition={replayPositions[replayCurrentIndex] || replayPositions[0]}
        onClose={handleStopReplay}
          />
        )}

        {/* Drawer lateral */}
        <VehicleDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          devices={devices}
          selectedDeviceId={selectedDeviceId}
          onDeviceSelect={setSelectedDeviceId}
          companyId={companyId}
        />

        {/* RouteDetailsSheet - Painel fixo lateral esquerdo */}
        {selectedDevice && (
          <RouteDetailsSheet
            open={!!routeDetailsDeviceId}
            onOpenChange={handleRouteDetailsClose}
            deviceId={routeDetailsDeviceId!}
            device={selectedDevice}
            companyId={effectiveCompanyId!}
            onPolylineUpdate={handlePolylineUpdate}
            onColoredPolylineUpdate={handleColoredPolylineUpdate}
            onStartReplay={handleStartReplay}
          />
        )}

        {/* StatisticsFooter - Aparece APENAS quando RouteDetailsSheet aberto */}
        {routeDetailsDeviceId && deviceStats && (
          <StatisticsFooter
            totalDistance={deviceStats.totalDistance}
            maxSpeed={deviceStats.maxSpeed}
            totalMinutes={deviceStats.totalMinutes}
            idleMinutes={deviceStats.idleMinutes}
            speedingMinutes={0}
          />
        )}

        {/* GeofenceSheet - Sheet lateral esquerdo */}
        <GeofenceSheet
          open={geofenceSheetOpen}
          onOpenChange={setGeofenceSheetOpen}
          companyId={companyId}
          onCenterMap={(position) => {
            console.log('📍 Centralizando mapa em:', position);
            mapRef.current?.centerAndZoom(position.lat, position.lng, 16);
          }}
          onEnableDrawing={(type) => {
            console.log('🎯 Ativando modo desenho:', type);
            setDrawingMode(type);
          }}
          drawnGeofence={drawnGeofence}
        />
      </div>
    </GpsLicenseGate>
  );
}
